import React, { useState } from "react";
import {
  ActionIcon,
  Button,
  Card,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useToast } from "../../context/ToastContext";
import { UPFRONT_PAY_TYPE_LABELS } from "../RepManagement/repDealDisplay";

// Pay types driven by editable scale lines, and the subset of those whose lines
// carry a dollar amount rather than a percentage of ACV.
const SCALE_TYPES = [
  "SCALE_AMOUNT_PER_ACCOUNT",
  "SCALE_AMOUNT_TOTAL",
  "SCALE_PERCENT_ACV",
];
const AMOUNT_SCALE_TYPES = ["SCALE_AMOUNT_PER_ACCOUNT", "SCALE_AMOUNT_TOTAL"];
const FIXED_PERCENT_TYPES = ["FIXED_PERCENT_ACV", "FIXED_PERCENTAGE_ACV"];

// Pull the type value off a ps_company_upfront_types entry, tolerating the few
// keys the overview may use.
const getCompanyUpfrontType = entry =>
  entry?.type ??
  entry?.upfront_pay_type_category ??
  entry?.upfront_type_category ??
  entry?.upfront_pay_type ??
  null;

const isCompanyTypeActive = entry =>
  entry?.is_active !== false && entry?.active !== false;

const emptyScaleLine = () => ({ accounts: "", value: "" });

// Form for adding an upfront pay, mirroring the iOS UpfrontFormView. It builds
// the upfront pay in the deal payload shape and hands it back through onCreated
// — there is no API call, since an upfront pay only ever exists as part of a
// deal. The pay type options come from the company's active upfront types.
function AddUpfrontPayForm({
  onCreated,
  onCancel,
  companyUpfrontTypes = null,
  initial = null,
}) {
  const { showError } = useToast();

  const payTypeOptions = (
    Array.isArray(companyUpfrontTypes) ? companyUpfrontTypes : []
  )
    .filter(isCompanyTypeActive)
    .map(entry => {
      const value = getCompanyUpfrontType(entry);
      return value
        ? { value, label: UPFRONT_PAY_TYPE_LABELS[value] ?? value }
        : null;
    })
    .filter(Boolean);

  const [payType, setPayType] = useState(initial?.upfront_pay_type ?? null);
  const [fixedAmount, setFixedAmount] = useState(initial?.fixed_amount ?? "");
  const [fixedPercentage, setFixedPercentage] = useState(
    initial?.fixed_percentage ?? ""
  );
  const [scaleLines, setScaleLines] = useState(() => {
    const lines = Array.isArray(initial?.scale_lines)
      ? initial.scale_lines
      : [];
    return lines.length
      ? lines.map(line => ({
          accounts: line.accounts_amount ?? "",
          value: line.amount ?? line.percentage ?? "",
        }))
      : [emptyScaleLine()];
  });

  const showScale = SCALE_TYPES.includes(payType);
  const isAmountScale = AMOUNT_SCALE_TYPES.includes(payType);

  const updateLine = (index, field, value) =>
    setScaleLines(prev =>
      prev.map((l, i) => (i === index ? { ...l, [field]: value } : l))
    );
  const addLine = () => setScaleLines(prev => [...prev, emptyScaleLine()]);
  const removeLine = index =>
    setScaleLines(prev => prev.filter((_, i) => i !== index));

  // Only the fields the deal-create DTO accepts — the global whitelist
  // validation rejects anything else.
  const buildUpfrontPay = () => {
    const payload = {
      upfront_pay_type: payType,
      fixed_amount: null,
      fixed_percentage: null,
    };
    if (payType === "FIXED_AMOUNT_PER_ACCOUNT") {
      payload.fixed_amount = Number(fixedAmount);
    } else if (FIXED_PERCENT_TYPES.includes(payType)) {
      payload.fixed_percentage = Number(fixedPercentage);
    } else if (showScale) {
      payload.scale_lines = scaleLines
        .filter(l => l.accounts !== "" && l.value !== "")
        .map(l => ({
          accounts_amount: Number(l.accounts),
          amount: isAmountScale ? Number(l.value) : null,
          percentage: isAmountScale ? null : Number(l.value),
        }));
    }
    return payload;
  };

  const handleSave = () => {
    if (!payType) {
      showError("Please select a pay type");
      return;
    }
    if (payType === "FIXED_AMOUNT_PER_ACCOUNT" && fixedAmount === "") {
      showError("Please enter a fixed amount");
      return;
    }
    if (FIXED_PERCENT_TYPES.includes(payType) && fixedPercentage === "") {
      showError("Please enter a fixed percentage");
      return;
    }
    if (
      showScale &&
      !scaleLines.some(l => l.accounts !== "" && l.value !== "")
    ) {
      showError("Please enter at least one scale line");
      return;
    }
    if (onCreated) onCreated(buildUpfrontPay());
  };

  return (
    <Card padding="md" radius="md" withBorder>
      <Stack gap="md">
        <Text size="sm" fw={600}>
          {initial ? "Edit Upfront Pay" : "New Upfront Pay"}
        </Text>

        {payTypeOptions.length === 0 && (
          <Text size="xs" c="dimmed">
            No active upfront pay types. Enable some under Pay Detail Defaults →
            Upfront Pay first.
          </Text>
        )}

        <Select
          label="Pay Type"
          placeholder="Select pay type"
          data={payTypeOptions}
          value={payType}
          onChange={setPayType}
          searchable
          required
        />

        {payType === "FIXED_AMOUNT_PER_ACCOUNT" && (
          <NumberInput
            label="Fixed Amount"
            placeholder="Enter fixed amount"
            value={fixedAmount}
            onChange={setFixedAmount}
            min={0}
            prefix="$"
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
          />
        )}

        {FIXED_PERCENT_TYPES.includes(payType) && (
          <NumberInput
            label="Fixed Percentage"
            placeholder="Enter fixed percentage"
            value={fixedPercentage}
            onChange={setFixedPercentage}
            min={0}
            suffix="%"
          />
        )}

        {showScale && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Scale
            </Text>
            <Group gap="xs" wrap="nowrap">
              <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                Account Number
              </Text>
              <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                {isAmountScale ? "Amount" : "Percentage of ACV"}
              </Text>
              <div style={{ width: 28 }} />
            </Group>
            {scaleLines.map((line, index) => (
              <Group key={index} gap="xs" wrap="nowrap" align="center">
                <NumberInput
                  placeholder="Account number"
                  value={line.accounts}
                  onChange={value => updateLine(index, "accounts", value)}
                  min={0}
                  style={{ flex: 1 }}
                />
                <NumberInput
                  placeholder={isAmountScale ? "Amount" : "Percentage"}
                  value={line.value}
                  onChange={value => updateLine(index, "value", value)}
                  min={0}
                  style={{ flex: 1 }}
                  {...(isAmountScale
                    ? {
                        prefix: "$",
                        thousandSeparator: ",",
                        decimalScale: 2,
                        fixedDecimalScale: true,
                      }
                    : { suffix: "%" })}
                />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => removeLine(index)}
                  disabled={scaleLines.length === 1}
                  aria-label="Remove scale line"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
            <Group>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={addLine}
              >
                Add Scale Line
              </Button>
            </Group>
          </Stack>
        )}

        <Group justify="flex-end">
          {onCancel && (
            <Button variant="subtle" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button onClick={handleSave}>
            {initial ? "Save Upfront Pay" : "Add Upfront Pay"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

export default AddUpfrontPayForm;
